import React, { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@mantine/core';
import { IconAugmentedReality } from '@tabler/icons-react';
import { THEME_COLORS } from '../../theme/ThemeConstants';
import '@google/model-viewer';

// `<model-viewer>` is a custom element registered at runtime by @google/model-viewer.
// Declare the subset of attributes we use so TSX accepts it.
type ModelViewerElement = HTMLElement & { loaded?: boolean };

type ModelViewerAttributes = React.DetailedHTMLProps<React.HTMLAttributes<ModelViewerElement>, ModelViewerElement> & {
  src?: string;
  'ios-src'?: string;
  poster?: string;
  alt?: string;
  ar?: boolean;
  'ar-modes'?: string;
  'camera-controls'?: boolean;
  'camera-orbit'?: string;
  'auto-rotate'?: boolean;
  'auto-rotate-delay'?: string;
  'rotation-per-second'?: string;
  'interaction-prompt'?: string;
  'shadow-intensity'?: string;
  exposure?: string;
  'environment-image'?: string;
  loading?: string;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': ModelViewerAttributes;
    }
  }
}

interface ModelViewerProps {
  glbUrl: string;
  usdzUrl?: string;
  posterUrl?: string;
  alt?: string;
  cameraDistance?: number;
  // Compact mode for product cards: auto-rotates, no controls/AR/hint, clicks pass through.
  compact?: boolean;
}

export const ModelViewer: React.FC<ModelViewerProps> = ({
  glbUrl,
  usdzUrl,
  posterUrl,
  alt,
  cameraDistance = 120,
  compact = false
}) => {
  const viewerRef = useRef<ModelViewerElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Keep the model hidden until it has fully loaded, then fade it in.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    setLoaded(!!viewer.loaded);
    const handleLoad = () => setLoaded(true);
    viewer.addEventListener('load', handleLoad);
    return () => viewer.removeEventListener('load', handleLoad);
  }, [glbUrl]);

  const frameStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100%'
  };

  return (
    <Box style={frameStyle}>
      <model-viewer
        ref={viewerRef}
        src={glbUrl}
        ios-src={compact ? undefined : usdzUrl}
        poster={compact ? undefined : posterUrl}
        alt={alt}
        camera-controls={compact ? undefined : true}
        camera-orbit={`0deg 75deg ${cameraDistance}%`}
        auto-rotate
        auto-rotate-delay='0'
        rotation-per-second='24deg'
        interaction-prompt={compact ? 'none' : 'auto'}
        shadow-intensity='1'
        exposure='1'
        environment-image='neutral'
        ar={compact ? undefined : true}
        ar-modes={compact ? undefined : 'webxr scene-viewer quick-look'}
        loading='eager'
        style={{
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
          padding: compact ? '6%' : '22%',
          backgroundColor: 'transparent',
          pointerEvents: compact ? 'none' : 'auto',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.4s ease'
        }}
      >
        {/* Replace model-viewer's default loading bar with nothing. */}
        <div slot='progress-bar' />
      </model-viewer>

      {!compact && (
        <Box
          style={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 999,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            pointerEvents: 'none',
            zIndex: 3
          }}
        >
          <IconAugmentedReality size={14} color={THEME_COLORS.accent.orange} />
          <Text size='10px' fw={600} c='white' tt='uppercase' style={{ letterSpacing: '0.06em' }}>
            Drag to rotate
          </Text>
        </Box>
      )}
    </Box>
  );
};
